import React, { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import formate from 'date-format'
import Loader from '../../components/shared/Loader'
import Record from '../../components/shared/Record'
import useFetch from '../../hooks/useFetch'
import ViewLayout from '../../layouts/ViewLayout'
import { getAdminQuoteCalculationById } from '../../utils/Apis/quotes'
import { formatQuoteType } from '../Quotes/COLUMNS'

/**
 * 判断是否为简单可展示值。
 * @param {unknown} value 原始值
 * @returns {boolean} 是否简单值
 */
const isSimpleValue = (value) => {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}

/**
 * 将对象转换为扁平参数行。
 * @param {Object} source 源对象
 * @param {string} prefix 字段前缀
 * @returns {Array<{key: string, value: unknown}>} 参数行
 */
const flattenObject = (source = {}, prefix = '') => {
  return Object.entries(source || {}).flatMap(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key
    if (isSimpleValue(value)) {
      return [{ key: nextKey, value }]
    }
    if (Array.isArray(value)) {
      return [{ key: nextKey, value: value.join(', ') }]
    }
    return flattenObject(value, nextKey)
  })
}

/**
 * 格式化简单值用于展示。
 * @param {unknown} value 原始值
 * @returns {string} 展示文本
 */
const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/**
 * 后台报价计算记录详情页。
 */
const ViewQuoteCalculation = () => {
  const { id } = useParams()

  const { data, error, loading } = useFetch({
    fn: () => getAdminQuoteCalculationById(id),
  })

  const specRows = useMemo(
    () => flattenObject(Object.keys(data?.spec || {}).length ? data?.spec : data?.pcbSpec || {}),
    [data],
  )
  const pricingRows = useMemo(() => flattenObject(data?.pricing || {}), [data])

  useEffect(() => {
    document.title = 'Quote Calculation Details - Admin'
  }, [])

  if (loading) return <Loader />
  if (error) return <div>Error: {error.message}</div>

  return (
    <ViewLayout
      sectionTitle={'Quote Calculation Details'}
      allButtonPath={'/quote-calculations'}
      allButtonTitle={'All Calculation Records'}
      className={'gap-8 md:flex md:flex-wrap lg:px-10'}
    >
      <Column title={'Basic Information'}>
        <Record label={'Quote Type'} value={formatQuoteType(data?.quoteType)} />
        <Record
          label={'Calculated At'}
          value={data?.createdAt ? formate.asString('dd/MM/yyyy HH:mm', new Date(data.createdAt)) : '-'}
        />
        <Record label={'Session ID'} value={data?.sessionId} />
        <Record label={'IP Address'} value={data?.ipAddress} />
        <Record label={'Pricing Version'} value={data?.pricingRuleVersion} />
      </Column>

      <Column title={'Customer'}>
        <Record label={'Name'} value={data?.customerInfo?.name} />
        <Record label={'Email'} value={data?.customerInfo?.email} />
        <Record label={'Phone'} value={data?.customerInfo?.phone} />
        <Record label={'Company'} value={data?.customerInfo?.company} />
        <Record label={'Country Code'} value={data?.customerInfo?.countryCode || data?.shipping?.countryCode} />
      </Column>

      <Column title={'Pricing Result'}>
        <Record label={'Currency'} value={data?.pricing?.currency || 'USD'} />
        <Record label={'PCB / Service Cost'} value={`$${Number(data?.pricing?.pcbCost || 0).toFixed(2)}`} />
        <Record label={'Shipping Cost'} value={`$${Number(data?.pricing?.shippingCost || 0).toFixed(2)}`} />
        <Record label={'Discount'} value={`$${Number(data?.pricing?.discountAmount || 0).toFixed(2)}`} />
        <Record label={'Total'} value={`$${Number(data?.pricing?.totalAmount || 0).toFixed(2)}`} />
        <Record label={'Lead Time'} value={`${data?.pricing?.leadTimeDays || 0} days`} />
        <Record label={'Shipping Method'} value={data?.pricing?.shippingMethod} />
      </Column>

      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
        <Panel title={'Build Time Options'}>
          {(data?.pricing?.deliveryOptions || []).length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {data.pricing.deliveryOptions.map((option) => {
                const isSelected = option.id === data?.pricing?.selectedDeliveryOption?.id
                return (
                  <div
                    key={option.id}
                    className={`rounded border p-3 ${
                      isSelected ? 'border-orange-400 bg-orange-50' : 'border-primary/10'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold">{option.label}</p>
                        <p className="text-xs text-muted-foreground">{option.description}</p>
                      </div>
                      <strong>${Number(option.manufacturingCost || 0).toFixed(2)}</strong>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {option.leadTimeDays} working days / adjustment ${Number(option.surcharge || 0).toFixed(2)}
                    </p>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No build time options</p>
          )}
        </Panel>

        <Panel title={'Cost Breakdown'}>
          {(data?.pricing?.details || []).length > 0 ? (
            <div className="grid grid-cols-1 gap-2">
              {data.pricing.details.map((item, index) => (
                <div key={`${item.label}-${index}`} className="flex justify-between rounded border border-primary/10 p-2">
                  <span>{item.label}</span>
                  <strong>${Number(item.amount || 0).toFixed(2)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No cost breakdown</p>
          )}
        </Panel>
      </div>

      <div className="w-full">
        <h2 className="mb-3 text-2xl font-semibold">Quote Options Snapshot</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {specRows.map((row) => (
            <div key={row.key} className="rounded border border-primary/10 p-3">
              <p className="text-xs text-muted-foreground">{row.key}</p>
              <p className="break-words text-sm">{formatValue(row.value)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="w-full">
        <h2 className="mb-3 text-2xl font-semibold">Pricing Raw Fields</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {pricingRows.map((row) => (
            <div key={row.key} className="rounded border border-primary/10 p-3">
              <p className="text-xs text-muted-foreground">{row.key}</p>
              <p className="break-words text-sm">{formatValue(row.value)}</p>
            </div>
          ))}
        </div>
      </div>
    </ViewLayout>
  )
}

const Column = ({ title, children }) => {
  return (
    <div className="flex min-w-[260px] flex-col gap-2">
      <h2 className="text-2xl font-semibold">{title}</h2>
      {children}
    </div>
  )
}

const Panel = ({ title, children }) => {
  return (
    <div className="rounded border border-primary/10 p-4">
      <h2 className="mb-3 text-xl font-semibold">{title}</h2>
      {children}
    </div>
  )
}

export default ViewQuoteCalculation
